import { ReactElement, useMemo } from 'react'
import { Vector3 } from 'three'
import { useTheme } from '@mui/material'
import { formatTwoDecimals } from 'src/utils/numberFormatters'
import SceneLineMarker from '../SceneLineMarker'
import Text3DThree from '../Text3DThree'

interface Props {
  /** Corners P0 to P3 of a rectangular area load. */
  points: { x: number; y: number; z: number }[]
}

/** Distance of the dimension lines from the load edges */
const DIMENSION_OFFSET = 0.3
/** How far the extension lines reach beyond the dimension lines */
const EXTENSION_OVERSHOOT = 0.1
/** Lift above the element so the markers are not hidden in it */
const LIFT = 0.02
const LABEL_GAP = 0.2
const TEXT_SCALE = 0.15

const GLOBAL_Z = new Vector3(0, 0, 1)

/**
 * Dimensions of a rectangular area load: l1 along the edge P0 → P1, l2 along the edge P1 → P2
 * and the origin P0.
 */
const AreaLoadDimensions = ({ points }: Props): ReactElement | null => {
  const { palette } = useTheme()

  const dimensions = useMemo(() => {
    if (points.length !== 4) return undefined

    const [p0, p1, p2] = points.map(({ x, y, z }) => new Vector3(x, y, z))

    const edge1 = p1.clone().sub(p0)
    const edge2 = p2.clone().sub(p1)
    const l1 = edge1.length()
    const l2 = edge2.length()

    if (l1 === 0 || l2 === 0) return undefined

    const u = edge1.divideScalar(l1)
    const v = edge2.divideScalar(l2)
    const normal = u.clone().cross(v).normalize()

    const lift = (point: Vector3) => point.clone().addScaledVector(normal, LIFT)

    // the l1 dimension sits outside the edge P0 → P1, the l2 dimension outside the edge P1 → P2
    const dimensionLine = (start: Vector3, end: Vector3, outward: Vector3) => {
      const offset = outward.clone().multiplyScalar(DIMENSION_OFFSET)
      const overshoot = outward.clone().multiplyScalar(DIMENSION_OFFSET + EXTENSION_OVERSHOOT)

      return {
        lines: [
          [lift(start.clone().add(offset)), lift(end.clone().add(offset))],
          [lift(start), lift(start.clone().add(overshoot))],
          [lift(end), lift(end.clone().add(overshoot))],
        ],
        labelPosition: lift(
          start
            .clone()
            .add(end)
            .multiplyScalar(0.5)
            .addScaledVector(outward, DIMENSION_OFFSET + LABEL_GAP),
        ),
      }
    }

    return {
      l1,
      l2,
      u,
      v,
      dimension1: dimensionLine(p0, p1, v.clone().negate()),
      dimension2: dimensionLine(p1, p2, u),
      originLabelPosition: lift(
        p0.clone().addScaledVector(u.clone().add(v).normalize(), -LABEL_GAP),
      ),
    }
  }, [points])

  if (!dimensions) return null

  const { l1, l2, u, v, dimension1, dimension2, originLabelPosition } = dimensions

  // each label runs along its edge
  const labels = [
    { text: `l1 = ${formatTwoDecimals(l1)} m`, position: dimension1.labelPosition, textX: u },
    { text: `l2 = ${formatTwoDecimals(l2)} m`, position: dimension2.labelPosition, textX: v },
    { text: 'P0', position: originLabelPosition, textX: u },
  ]

  return (
    <>
      <SceneLineMarker
        lines={[...dimension1.lines, ...dimension2.lines]}
        color={palette.text.primary}
        lineWidth={2}
        disableDepthTest
      />
      {labels.map(({ text, position, textX }) => (
        <Text3DThree
          key={text}
          textLines={[text]}
          color={palette.text.primary}
          backgroundColor={palette.common.white}
          borderColor={palette.text.primary}
          borderWidth={0.005}
          position={position}
          anchorLocation="center"
          scale={TEXT_SCALE}
          textXOrientation={textX}
          textYOrientation={GLOBAL_Z.clone().cross(textX).normalize()}
          disableDepthTest
        />
      ))}
    </>
  )
}

export default AreaLoadDimensions
